// Cross-window session bus.
//
// Every tab, popup and PWA window of this origin shares ONE server-side
// session, but until now each ran as if it were alone:
//
//   - `doRefresh()` reads the refresh token from the in-memory store, and
//     zustand's `persist` does not sync across windows. So window A could
//     rotate the token, write the new one to localStorage, and window B would
//     keep the superseded one in memory. When B's turn came it refreshed with
//     a token the server had already retired; past the 120 s grace window
//     (REFRESH_GRACE_SECONDS in auth-service) that is a SESSION_NOT_FOUND —
//     surfaced to the user as "sessione chiusa da un'altra postazione". This
//     is the mechanism behind session losses that looked random: they depended
//     on how the two windows' refresh timers happened to interleave.
//
//   - Each window ran its own inactivity countdown. Working in window A did
//     not reset window B's timer, so the window holding the softphone could
//     time out while the user was demonstrably at the keyboard.
//
// Both are fixed here: token rotations and user activity are broadcast, and
// rotation is serialised through a Web Lock so only one window ever calls
// /auth/refresh. BroadcastChannel never echoes to the sender, so a handler
// reacting to a message can't feed its own broadcast back into itself.
//
// Everything degrades safely: without BroadcastChannel or navigator.locks
// (older Safari) the app behaves exactly as it did before.

const CHANNEL_NAME = 'wfm-session';
const REFRESH_LOCK = 'wfm-session-refresh';

export const SESSION_MSG = {
  tokens: 'tokens',       // a rotation landed — adopt the new pair
  activity: 'activity',   // the user did something in another window
  logout: 'logout',       // session ended deliberately
  lock: 'lock',           // screen locked (inactivity)
  unlock: 'unlock',       // screen unlocked after re-auth
  dial: 'dial',           // click-to-dial aimed at whichever window holds the phone
  callState: 'call-state',// the softphone's call state changed
  settings: 'settings',   // branding / security settings were saved
  theme: 'theme',         // appearance (light/dark + token set) changed
  impersonation: 'impersonation', // SUPER_ADMIN started or ended impersonation
};

let _channel;
let _unsupported = false;

function channel() {
  if (_unsupported) return null;
  if (_channel) return _channel;
  try {
    if (typeof BroadcastChannel === 'undefined') { _unsupported = true; return null; }
    _channel = new BroadcastChannel(CHANNEL_NAME);
  } catch (_) {
    _unsupported = true;
    return null;
  }
  return _channel;
}

// Fire-and-forget. A failed post must never break the caller's flow — the
// worst case is the pre-existing single-window behaviour.
export function broadcast(type, payload = {}) {
  const c = channel();
  if (!c) return;
  try { c.postMessage({ type, payload, at: Date.now() }); } catch (_) { /* non-fatal */ }
}

// Subscribe to one message type. Returns an unsubscribe function.
export function onSessionMessage(type, handler) {
  const c = channel();
  if (!c) return () => {};
  const listener = (ev) => {
    const msg = ev?.data;
    if (!msg || msg.type !== type) return;
    try { handler(msg.payload, msg.at); } catch (_) { /* a bad handler must not kill the bus */ }
  };
  c.addEventListener('message', listener);
  return () => { try { c.removeEventListener('message', listener); } catch (_) {} };
}

// Serialise token rotation across windows. Whoever holds the lock is the only
// window talking to /auth/refresh; the others queue and — thanks to the
// staleness check in doRefresh — find the work already done when they get in.
//
// Without navigator.locks this is a straight pass-through: the broadcast in
// doRefresh still narrows the race to the duration of one HTTP call, which is
// far inside the server's 120 s grace window.
export async function withRefreshLock(fn) {
  try {
    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return await navigator.locks.request(REFRESH_LOCK, fn);
    }
  } catch (_) {
    // Lock unavailable (private mode, unsupported) — fall through.
  }
  return fn();
}
